import { useLocation, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eyebrow } from '../components/Eyebrow';
import { StatusChip } from '../components/StatusChip';
import { Button } from '../components/Button';
import { Card } from '../components/Card';

interface ResultState {
  submittedAt?: string;
  attachments?: Array<{ template: string; filename: string; sizeKB: number }>;
  email?: string;
  applicant?: string;
}

/**
 * Property Insurance Quote — thank-you / submission-received page.
 *
 * Rendered after `POST /property/submit` succeeds. Displays the submission
 * id, expected broker-follow-up window, an echo of what was submitted, and
 * the required disclaimers (not-a-quote, warranty acknowledgment).
 *
 * See docs/property/PRD.md §6.4 and §7 (user journey).
 */
export function PropertyResult() {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const state = (location.state ?? {}) as ResultState;

  const submissionId = params.id ?? '';
  const shortId = submissionId.slice(-6).toUpperCase();
  const submittedAt = state.submittedAt
    ? new Date(state.submittedAt).toLocaleString()
    : '';

  return (
    <>
      <Navbar />
      <main className="container-site py-20">
        <div className="flex flex-col gap-10 max-w-3xl">
          <div className="flex flex-col gap-6">
            <StatusChip
              label={`SUBMISSION_RECEIVED · SEQ_${shortId}`}
              tone="done"
            />
            <h1 className="font-display font-medium tracking-[-0.02em] leading-[1.05] text-[clamp(36px,5vw,56px)]">
              Your application is in.
            </h1>
            <p className="max-w-prose text-ink-muted text-[1.0625rem] leading-relaxed">
              Derek Krenke just received your submission with every ACORD
              form prefilled and Zachary's internal supplements attached.
              He or Art will be in touch within <strong>two business days</strong>
              with a formal quote. You'll receive a copy at{' '}
              {state.email ? (
                <span className="font-mono text-[0.875rem] text-ink">
                  {state.email}
                </span>
              ) : (
                'the email you provided'
              )}
              .
            </p>
            <Eyebrow>FOLLOWUP · 2_BUSINESS_DAYS</Eyebrow>
          </div>

          <Card className="p-6 flex flex-col gap-4">
            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.125em] text-ink-subtle">
              &gt; SUBMISSION_RECORD
            </span>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 font-mono text-[0.8125rem]">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-ink-subtle tracking-[0.08em]">&gt; ID</dt>
                <dd className="text-ink tabular-nums">{submissionId}</dd>
              </div>
              {submittedAt ? (
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-ink-subtle tracking-[0.08em]">&gt; SUBMITTED</dt>
                  <dd className="text-ink">{submittedAt}</dd>
                </div>
              ) : null}
              {state.applicant ? (
                <div className="flex items-center justify-between gap-4 md:col-span-2">
                  <dt className="text-ink-subtle tracking-[0.08em]">&gt; APPLICANT</dt>
                  <dd className="text-ink text-right">{state.applicant}</dd>
                </div>
              ) : null}
            </dl>

            {state.attachments && state.attachments.length > 0 ? (
              <>
                <div className="h-px bg-hairline" />
                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[0.6875rem] uppercase tracking-[0.125em] text-ink-subtle">
                    &gt; SENT_TO_DEREK ({state.attachments.length}_FILES)
                  </span>
                  <ul className="flex flex-col gap-1 font-mono text-[0.8125rem]">
                    {state.attachments.map((a) => (
                      <li
                        key={a.filename}
                        className="flex items-center justify-between gap-4 text-ink"
                      >
                        <span>{a.filename}</span>
                        <span className="text-ink-subtle tabular-nums">
                          {a.sizeKB} KB
                        </span>
                      </li>
                    ))}
                    <li className="flex items-center justify-between gap-4 text-ink">
                      <span>submission.json</span>
                      <span className="text-ink-subtle">raw answers</span>
                    </li>
                  </ul>
                </div>
              </>
            ) : null}
          </Card>

          <div className="border-t border-hairline pt-6 flex flex-col gap-3">
            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.125em] text-ink-subtle">
              &gt; DISCLAIMERS
            </span>
            <p className="font-display text-[0.875rem] text-ink-muted leading-relaxed">
              This submission is an <em>application</em> for insurance —{' '}
              <strong>not a bindable quote</strong>. Any coverage is{' '}
              <strong>subject to underwriting</strong> by a carrier, and the
              applicant warrants the answers above are true and accurate to
              the best of their knowledge. The warranted application becomes
              part of any policy ultimately issued.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button as="link" to="/" variant="secondary">
              Return home
            </Button>
            <Button as="link" to="/indication" variant="ghost">
              Also need builders risk?
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default PropertyResult;
